import {Inject, Injectable} from '@angular/core';
import {Unit} from '../unit';
import {BehaviorSubject, Observable, of, Subscription, timer} from 'rxjs';
import {catchError, concatMap, delay, expand, filter, finalize, map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {WebStompService} from '../../../web-stomp/web-stomp.service';
import {RestFilterModel} from 'gelios-ng-admin-components';
import {HttpClient} from '@angular/common/http';
import {UnitStatusModel} from '../../../unit-status-manager/unit-status.model';
import {LatLngBounds} from 'leaflet';
import {DashboardFilter} from '../dashboard-filter';
import {StatisticsModel} from './statistics.model';
import {ProjectBusinessLogicFilterModel} from '../../../shared/project-business-logic-filter/project-business-logic-filter.model';
import {updateRestFilterModel} from '../../../shared/project-business-logic-filter/project-business-logic-filter-utils';
import {ImmutableListRepositoryService} from '../../../../lib/gelios-field/immutable-list-repository.service';

@Injectable({
  providedIn: 'root'
})
export class DashboardUnitCacheService {
  public static readonly pageSize = 1000;
  protected _itemsReceivedCount: number = null;
  protected _itemsTotalCount: number = null;
  constructor(
    public http: HttpClient,
    protected webStompService: WebStompService,
    @Inject('ENVIRONMENT_VERSION') protected environmentVersion,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint,
    protected immutableList: ImmutableListRepositoryService
  ) { }

  public get itemsReceivedCount() {
    return this._itemsReceivedCount;
  }
  public get itemsTotalCount() {
    return this._itemsTotalCount;
  }

  leafletBoundaryBox: LatLngBounds;

  protected skipUnitIfRefreshInProgress: {};
  webStompUnitCache: { [id: string]: Unit };
  public unitsCache = new BehaviorSubject<Unit[]>([]);
  unitsCacheIsLoading = new BehaviorSubject<boolean>(false);
  public statistics = new BehaviorSubject<StatisticsModel>({});
  protected unitRefreshDataObservable: Observable<any>;
  protected unitRefreshPollDataObservable: Observable<any>;
  protected unitRefreshPollDataSubscription: Subscription;
  protected unitListRefreshSubscription: Subscription;
  protected webStompNewUnitMessageSubscription: Subscription;
  protected webStompCacheSyncSubscription: Subscription;
  // private restQuery = new BehaviorSubject(new RestQueryParams({page: 1, pageSize: DashboardUnitCacheService.pageSize}));

  protected  readonly coordEpsilon = 1e-8;

  protected readonly unitListRefreshInterval = 20000;

  private get unitTypeToggles(): { [key: string]: string } {
    const toggles = {};
    const unitTypes = this.immutableList.getListInstant('unit-type');
    if (unitTypes) {
      unitTypes.forEach(ut => {
        if (ut['unit_type_name']) {
          toggles[ut['unit_type_name']] = ut['unit_type_name'];
        }
      });
    }
    return toggles;
  }

  private readonly orderStatusToggles = {
    showOrderStatusFree: Unit.STATUS_ORDER_GRID_FREE,
    showOrderStatusBooked: Unit.STATUS_ORDER_GRID_BOOKED,
    showOrderStatusActive: Unit.STATUS_ORDER_GRID_ACTIVE,
    showOrderStatusPaused: Unit.STATUS_ORDER_GRID_PAUSED,
    showOrderStatusNotAvailable: Unit.STATUS_ORDER_GRID_NOT_AVAILABLE
  };

  private readonly unitStatusToggles = {
    show_available: 'available',
    show_not_available: 'not_available',
    show_on_charge: 'on_charge',
    show_at_warehouse: 'at_warehouse',
    show_service_mode: 'service_mode',
    show_under_repair: 'under_repair',
    show_donor: 'donor',
    show_stolen: 'stolen',
  };

  private readonly connectionStatusToggles = {
    showOnline: Unit.STATUS_CONNECTION_ONLINE,
    showOnlineRecently: Unit.STATUS_CONNECTION_ONLINE_RECENTLY,
    showOnlineLately: Unit.STATUS_CONNECTION_ONLINE_LATELY,
    showOffline: Unit.STATUS_CONNECTION_OFFLINE,
  };

  private readonly gpsCoordinateStatusToggles = {
    showGpsCoordinatesOk: Unit.GPS_COORDINATE_STATUS_OK,
    showGpsCoordinatesInvalid: Unit.GPS_COORDINATE_STATUS_INVALID,
  };


  private readonly chargeStatusToggles = {
    showChargeLow: Unit.CHARGE_STATUS_ALARM,
    showChargeMediumWarning: Unit.CHARGE_STATUS_WARNING,
    showChargeMedium: Unit.CHARGE_STATUS_OK,
    showChargeHigh: Unit.CHARGE_STATUS_PERFECT,
  };

  private readonly lastOrderFinishTimeStatusToggles = {
    showLastOrderFinishTimeStatusPerfect: Unit.STATUS_LAST_ORDER_FINISH_TIME_PERFECT,
    showLastOrderFinishTimeStatusOk: Unit.STATUS_LAST_ORDER_FINISH_TIME_OK,
    showLastOrderFinishTimeStatusWarning: Unit.STATUS_LAST_ORDER_FINISH_TIME_WARNING,
    showLastOrderFinishTimeStatusAlarm: Unit.STATUS_LAST_ORDER_FINISH_TIME_ALARM,
  };

  private readonly inRideAllowArea = {
    showRideAllow: 'true',
    showRideRestrict: 'false',
  };
  private readonly inFinishAllowArea = {
    showFinishAllow: 'true',
    showFinishRestrict: 'false',
  };

  protected setWebStompSync(syncInterval: number = 1000) {
    if (this.webStompCacheSyncSubscription) {
      this.webStompCacheSyncSubscription.unsubscribe();
    }
    if (this.webStompCacheSyncSubscription) {
      this.webStompCacheSyncSubscription.unsubscribe();
      this.webStompCacheSyncSubscription = null;
    }
    this.webStompCacheSyncSubscription =
      timer(syncInterval, syncInterval).pipe(
        withLatestFrom(
          this.webStompService.isOnline,
          this.webStompService.isEnabled,
          this.unitsCache
        ),
        filter(
          ([timerNumber, isOnline, isEnabled, unitsCache]) => isOnline && isEnabled && unitsCache.length > 0
        )
      ).subscribe(
        ([timerNumber, isOnline, isEnabled, unitsCache]) => this.syncUnitCacheToWebStompCache(unitsCache)
      );
  }

  protected getSyncIntervalMsec(unitCount: number = 1000) {
    let syncInterval = 1000;
    if (unitCount > 2000) {
      syncInterval = 5000;
    } else if (unitCount > 1000) {
      syncInterval = 2000;
    }
    return syncInterval;
  }

  applyFilter(
    filterModel: RestFilterModel,
    dashBoardFilter: DashboardFilter,
    filterToggles: {[toggles: string]: string},
    filterName: string
  ) {
    const filterTogglesKeys = Object.keys(filterToggles);
    const filterArray = filterTogglesKeys
      .filter(filterToggle => dashBoardFilter[filterToggle])
      .map(filterToggle => filterToggles[filterToggle]);
    if (filterArray.length === filterTogglesKeys.length) {
      return;
    }
    const filterString = filterArray
      .join(',');
    if (filterString) {
      filterModel[filterName] = {

        type: 'equals',
        filter: filterString,
        filterType: 'text',
      };
    }
  }

  // applyChargeFilter(
  //   filterModel: RestFilterModel,
  //   dashBoardFilter: DashboardFilter
  // ) {
  //   if (dashBoardFilter.showChargeLow) {
  //     filterModel.charge_from = {
  //       type: 'equals',
  //       filter: 0,
  //       filterType: 'number',
  //     };
  //     filterModel.charge_to = {
  //       type: 'equals',
  //       filter: Unit.CHARGE_LOW - 1,
  //       filterType: 'number',
  //     };
  //     return;
  //   }
  //   if (dashBoardFilter.showChargeMediumWarning) {
  //     filterModel.charge_from = {
  //       type: 'equals',
  //       filter: Unit.CHARGE_LOW,
  //       filterType: 'number',
  //     };
  //     filterModel.charge_to = {
  //       type: 'equals',
  //       filter: Unit.CHARGE_MEDIUM_WARNING - 1,
  //       filterType: 'number',
  //     };
  //     return;
  //   }
  //   if (dashBoardFilter.showChargeMedium) {
  //     filterModel.charge_from = {
  //       type: 'equals',
  //       filter: Unit.CHARGE_MEDIUM_WARNING,
  //       filterType: 'number',
  //     };
  //     filterModel.charge_to = {
  //       type: 'equals',
  //       filter: Unit.CHARGE_MEDIUM - 1,
  //       filterType: 'number',
  //     };
  //     return;
  //   }
  //   if (dashBoardFilter.showChargeHigh) {
  //     filterModel.charge_from = {
  //       type: 'equals',
  //       filter: Unit.CHARGE_MEDIUM,
  //       filterType: 'number',
  //     };
  //     filterModel.charge_to = {
  //       type: 'equals',
  //       filter: 100,
  //       filterType: 'number',
  //     };
  //     return;
  //   }
  // }

  public start(
    leafletBoundaryBox: LatLngBounds, dashBoardFilter: DashboardFilter,
    projectBusinessLogicFilter: ProjectBusinessLogicFilterModel) {
    this.unitsCacheIsLoading.next(true);
    this.leafletBoundaryBox = leafletBoundaryBox;
    const filterModel: RestFilterModel = updateRestFilterModel({
      min_lat: {
        type: 'equals',
        filter: this.leafletBoundaryBox.getSouth().toString(),
        filterType: 'number'
      },
      min_lon: {
        type: 'equals',
        filter: this.leafletBoundaryBox.getWest().toString(),
        filterType: 'number'
      },
      max_lat: {
        type: 'equals',
        filter: this.leafletBoundaryBox.getNorth().toString(),
        filterType: 'number'
      },
      max_lon: {
        type: 'equals',
        filter: this.leafletBoundaryBox.getEast().toString(),
        filterType: 'number',
      },
      excludeTrackers: {
        type: 'equals',
        filter: '1',
        filterType: 'number',
      },
    }, projectBusinessLogicFilter);
    this.applyFilter(filterModel, dashBoardFilter, this.unitTypeToggles, 'unit_type_name');
    this.applyFilter(filterModel, dashBoardFilter, this.orderStatusToggles, 'status_order_grid');
    this.applyFilter(filterModel, dashBoardFilter, this.unitStatusToggles, 'unit_status_name');
    this.applyFilter(filterModel, dashBoardFilter, this.connectionStatusToggles, 'status_connection');
    this.applyFilter(filterModel, dashBoardFilter, this.gpsCoordinateStatusToggles, 'gpsCoordinateStatus');
    this.applyFilter(filterModel, dashBoardFilter, this.chargeStatusToggles, 'chargeStatus');
    this.applyFilter(filterModel, dashBoardFilter, this.lastOrderFinishTimeStatusToggles, 'lastOrderFinishTimeStatus');
    this.applyFilter(filterModel, dashBoardFilter, this.inRideAllowArea, 'inRideAllowArea');
    this.applyFilter(filterModel, dashBoardFilter, this.inFinishAllowArea, 'inFinishAllowArea');

    const paramFields = [
      'id', 'name', 'lastMsgUnit.lat', 'lastMsgUnit.lon', 'lastMsgUnit.time', 'unitModel.unit_type_name',
      'lastMsgUnit.charge',
      'status_connection', 'status_order', 'charge',
      'unitModel.project.custom_name',
      'hwModel.unit_type',
      'endUserOrder.id',
      'showInApplication',
      'lastOrderFinishTimeStatus',
      'hoursPassedSinceLastOrder',
      'unitStatus.id', 'unitStatus.name',
      'statusCombined',
      'status_order_grid',
      'unit_status_id',
      'gpsCoordinateStatus',
      'chargeStatus',
      'gpsIsOnline', 'inRideAllowArea', 'inFinishAllowArea',
    ].join(',');
    const paramExpand = [
      'unitModel', 'hwModel', 'unitType', 'lastMsgUnit', 'unitModel.project', 'endUserOrder',
      'unitStatus',
      'gpsIsOnline', 'inRideAllowArea', 'inFinishAllowArea',
    ].join(',');

    const unitSearch = Object.keys(filterModel).reduce(
      (accumulator, filterField) => {
        accumulator[filterField] = filterModel[filterField].filterType === 'number' ?
          parseFloat(filterModel[filterField].filter)
          : filterModel[filterField].filter;
        return accumulator;
      },
      {}
    );

    const params = {
      'per-page': 1000,
      fields: paramFields,
      expand: paramExpand,
      UnitSearch: unitSearch
    };


    this.unitRefreshDataObservable = this.http
      .post<any>(
        this.environmentApiBasepoint + 'unit-list-and-statistics',
        {
          params: params
        },
        {observe: 'body'}
      )
      .pipe(
        delay(0),
        tap(
          response => {
            this._itemsTotalCount = parseInt(response.totalCount, 10);
            this.statistics.next(response.statistics);
            this._itemsReceivedCount = (response.units as Array<any>).length;
          }
        ),
        map(response => (response.units as Array<any>).map(
          unit => {
            unit.sats = parseInt(unit.sats, 10);
            if (unit.lastMsgUnit) {
              unit.lastMsgUnit.charge = parseInt(unit.lastMsgUnit.charge, 10);
              unit.charge = parseInt(unit.charge, 10);
            }
            return unit;
          }
        )),
        catchError(
          error => null,
        ),
        tap(
          (units: Array<Unit>) => {
            if (units !== null) {
              if (Object.keys(this.skipUnitIfRefreshInProgress).length === 0) {
                this.unitsCache.next(units);
              } else {
                const existingUnits = [...this.unitsCache.getValue()];
                const newUnitsIndexed: any = {};
                for (const unit of units) {
                  newUnitsIndexed[unit.id] = unit;
                }
                // tslint:disable-next-line:prefer-for-of
                for (let i = 0; i < existingUnits.length; i++) {
                  if (!this.skipUnitIfRefreshInProgress[existingUnits[i].id]) {
                    existingUnits[i] = newUnitsIndexed[existingUnits[i].id];
                  } else {
                    delete this.skipUnitIfRefreshInProgress[existingUnits[i].id];
                  }
                }
                this.unitsCache.next(existingUnits);
              }
              if (!this.webStompUnitCache) {
                this.webStompUnitCache = {};
              }
              const syncIntervalMsec = 1000; // this.getSyncIntervalMsec(units.length);
              this.setWebStompSync(syncIntervalMsec);
              units.forEach(
                unit => this.webStompUnitCache[unit.id] = unit
              );
            }
          }
        ),
        finalize(() => this.unitsCacheIsLoading.next(false)),
      );
    this.unitRefreshPollDataObservable = of(null).pipe(
      withLatestFrom(
        this.webStompService.isOnline,
        this.webStompService.isEnabled
      ),
      switchMap(
        ([_, webStompIsOnline, webStompIsEnabled]) => {
          this.skipUnitIfRefreshInProgress = {};
          return this.unitRefreshDataObservable;
        }
      )
    );
    // this.initialUnitDataSubscription = this.unitRefreshDataObservable.subscribe();
    this.unitRefreshPollDataSubscription = this.unitRefreshPollDataObservable
      .pipe(
        expand(x => timer(this.unitListRefreshInterval).pipe(concatMap(y => this.unitRefreshPollDataObservable)))
      ).subscribe();
    this.webStompNewUnitMessageSubscription =
      this.webStompService.newUnitMessage.subscribe(
        message => {
          const unitId = message.id;
          if (this.webStompUnitCache && unitId in this.webStompUnitCache) {
            this.webStompUnitCache[unitId].charge = message.charge;
            this.webStompUnitCache[unitId].showInApplication = message.showInApplication;
            this.webStompUnitCache[unitId].status_connection = message.status_connection;
            this.webStompUnitCache[unitId].status_order = message.status_order;
            this.webStompUnitCache[unitId].lastOrderFinishTimeStatus = message.lastOrderFinishTimeStatus;
            this.webStompUnitCache[unitId].hoursPassedSinceLastOrder = message.hoursPassedSinceLastOrder;
            this.webStompUnitCache[unitId].lastMsgUnit.time = message.lastMsgUnit.time;
            if (Math.abs(message.lastMsgUnit.lat) > this.coordEpsilon && Math.abs(message.lastMsgUnit.lon) > this.coordEpsilon) {
              this.webStompUnitCache[unitId].lastMsgUnit.lat = message.lastMsgUnit.lat;
              this.webStompUnitCache[unitId].lastMsgUnit.lon = message.lastMsgUnit.lon;
            }
            this.webStompUnitCache[unitId].lastMsgUnit.charge = message.lastMsgUnit.charge;
            this.webStompUnitCache[unitId].lastMsgUnit.speed = message.lastMsgUnit.speed;
            this.webStompUnitCache[unitId].sats = message.sats;
            this.webStompUnitCache[unitId].gpsIsOnline = message.gpsIsOnline;
            this.webStompUnitCache[unitId].inRideAllowArea = message.inRideAllowArea;
            this.webStompUnitCache[unitId].inFinishAllowArea = message.inFinishAllowArea;
            this.webStompUnitCache[unitId].statusCombined = message.statusCombined;
            this.webStompUnitCache[unitId].status_order_grid = message.status_order_grid;
          }
        }
      );
  }
  public stop(clearCache = false) {
    [
      this.unitRefreshPollDataSubscription,
      this.webStompNewUnitMessageSubscription,
      this.unitListRefreshSubscription,
      this.webStompCacheSyncSubscription,
      // this.initialUnitDataSubscription,
    ].filter(s => !!s).forEach(s => s.unsubscribe());
    if (clearCache) {
      this.unitsCache.next([]);
      this.webStompUnitCache = {};
    }
  }
  protected syncUnitCacheToWebStompCache(unitsCache: Unit[]) {
    if (this.webStompUnitCache) {
      const newUnitsCache = unitsCache.map(
        unit => this.webStompUnitCache[unit.id]
      );
      this.unitsCache.next(newUnitsCache);
    }
  }

  public updateUnitStatus(unitId: number, status: UnitStatusModel) {
    this.skipUnitIfRefreshInProgress[unitId] = true;
    if (unitId in this.webStompUnitCache) {
      this.webStompUnitCache[unitId].unit_status_id = status.id;
      this.webStompUnitCache[unitId].showInApplication = status.show_in_application;
      const unitsCache = [...this.unitsCache.getValue()];
      for (const unit of unitsCache) {
        if (unit.id === unitId) {
          unit.unit_status_id = status.id;
          unit.showInApplication = status.show_in_application;
          break;
        }
      }
      this.unitsCache.next(unitsCache);
    }
  }
}
